import {
  assertCurrentSession, checkSession, expireSession, getSession, sessionChangedError,
} from './session'

export const BASE2 = '/api'

export const parseResponse = async (response) => {
  const text = await response.text()
  let data
  try {
    data = text ? JSON.parse(text) : {}
  } catch (_) {
    throw Object.assign(new Error('Invalid JSON'), { error: 'Invalid JSON', raw: text, status: response.status })
  }
  if (response.status >= 200 && response.status < 400) return data
  throw Object.assign(new Error(`Request failed (${response.status})`), {
    ...(data && typeof data === 'object' ? data : { message: data }),
    status: response.status,
    retryAfter: response.headers?.get?.('Retry-After'),
  })
}

// Both JSON and binary API requests use the same session boundary.
export const request = async (url, options = {}, readResponse = parseResponse) => {
  const target = new URL(url, window.location.origin)
  const isAppApi = target.origin === window.location.origin && target.pathname.startsWith('/api/')
  const isAuthEndpoint = target.pathname.startsWith('/api/auth/')
  const guarded = isAppApi && !isAuthEndpoint
  const before = getSession()
  const context = guarded ? checkSession() : before
  if (guarded && (context.ending || (before.token && before.generation !== context.generation))) {
    throw sessionChangedError()
  }
  let response
  try {
    response = await fetch(url, {
      ...(isAppApi ? { credentials: 'include', mode: 'cors' } : {}),
      ...options,
      headers: {
        ...(isAppApi ? { Accept: 'application/json', Authorization: `Bearer ${context.token || ''}` } : {}),
        ...options.headers,
      },
    })
  } catch (error) {
    if (guarded) assertCurrentSession(context)
    throw error
  }
  if (guarded) {
    assertCurrentSession(context)
    if (response.status === 401 && context.token) {
      expireSession(context)
      try {
        await parseResponse(response)
      } catch (error) {
        throw Object.assign(error, { status: response.status, sessionHandled: true })
      }
    }
  }
  try {
    const result = await readResponse(response)
    if (guarded) assertCurrentSession(context)
    return result
  } catch (error) {
    if (guarded) assertCurrentSession(context)
    throw error
  }
}

const jsonRequest = (method, url, data, options) => request(`${BASE2}${url}`, {
  method,
  ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
  ...options,
  headers: {
    ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
    ...options?.headers,
  },
})

const api = {
  get: (url, options) => jsonRequest('GET', url, undefined, options),
  post: (url, data, options) => jsonRequest('POST', url, data, options),
  patch: (url, data, options) => jsonRequest('PATCH', url, data, options),
  put: (url, data, options) => jsonRequest('PUT', url, data, options),
  delete: (url, options) => jsonRequest('DELETE', url, undefined, options),
}

export default api
